import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useController } from "react-hook-form";
import useSWR from "swr";
import axios from "axios";
import * as Sentry from "@sentry/react";
import { RegulaDocumentUpload } from "@simplecitizen/gdl-regula-components";
import { v4 as uuidv4 } from "uuid";
import { useAppDispatch, useAppSelector } from "src/hooks/redux-hooks";
import { useIsMobile } from "src/hooks/useIsMobile";
import { setDocumentValidationError } from "src/store/slices/documentValidationSlice";
import {
  addIdentityDoc,
  setDocCaptureData,
} from "src/store/slices/identityDocCapture";
import { compareGivenNames, compareLastNames,  findRegulaFieldBySlug } from "src/utils/utils";
import "@simplecitizen/gdl-regula-components/dist/components.css";

// Constants
const SWR_RETRY_COUNT = 5;
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://localhost:5003/api/workright";
const NAME_MISMATCH_MESSAGE = `The name(s) on the uploaded document do not match the Biographical Information in your WorkRight profile. You can update your WorkRight profile by ticking the relevant box below, or choose "Update All" to update both Given Name(s) and Surname.`;


// Types
interface RegulaUploadCardProps {
  document: DocumentCollector;
  control: any;
  removeDocument: (
    document: DocumentCollector,
    docToRemove: "nonIdentityDocuments" | "identityDocuments"
  ) => void;
}

const RegulaUploadCard = ({
  document,
  control,
  removeDocument,
}: RegulaUploadCardProps) => {
  // Redux state
  const dispatch = useAppDispatch();
  const questionnaire_data = useAppSelector(
    (state: any) => state?.questionnaire_data?.questionnaire
  );
  const client = useAppSelector(
    (state) => state.questionnaire_data.questionnaire?.client
  );
  const selectedIDDocument = useAppSelector(
    (state: any) => state?.identity_doc_capture?.selectedIDDocument
  );

  // Router hooks
  const { checkId } = useParams<{ checkId: string }>();
  const location = useLocation();

  // Form fields
  const { field: firstName } = useController({
    name: "firstName",
    control,
  });
  const { field: lastName } = useController({
    name: "lastName",
    control,
  });
  const { field: middleName } = useController({
    name: "middleName",
    control,
  });

  // Component state
  const [mobileUrl, setMobileUrl] = useState<string>();
  const [warningMessage, setWarningMessage] = useState<string>();
  const [images, setImages] = useState();
  const [faceMisMatch, setFaceMisMatch] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [disableStoreButton, setDisableStoreButton] = useState(false);
  const [sessionId, setSessionId] = useState<string>(() => uuidv4());
  const consumedSessions = useRef<Set<string>>(new Set());
  const [documentFields, setDocumentFields] = useState<any>(null);
  const [mobileHandoffFields, setMobileHandoffFields] = useState<any>(null);
  const [mobileHandoffImages, setMobileHandoffImages] = useState<any>(null);
  const [documentErrors, setDocumentErrors] = useState<any>(null);
  const [misMatchFields, setMisMatchFields] = useState<string[]>();
  const [docKey, setDocKey] = useState<any>(null);
  const [error, setIsError] = useState<string>();
  const [checkIfDocsUploaded, setCheckIfDocsUploaded] = useState(false);
  const [startFetching, setStartFetching] = useState(false);
  const [forceSubmit, setForceSubmit] = useState(false);
  const [disableForceSubmitButton, setDisableForceSubmitButton] =
    useState(false);
  const [unsuccessful, setUnsuccessful] = useState(0);
  const prevErrorRef = useRef("");

  const [mobileUploadStatus, setMobileUploadStatus] = useState<
    "initiated" | "success" | "error" | "loading"
  >("initiated");
  const isMobile = useIsMobile();


  useEffect(
    ()=>{ 
      setForceSubmit(!document?.config?.preventForcedSubmission)
    },[document?.config?.preventForcedSubmission]
  )

  // Helper function to update names from profile mismatch fields
  const updateNamesFromProfile = useCallback(
    (profileMisMatchFields: any[]) => {
      const givenNamesField = profileMisMatchFields?.find(
        (item: any) => item.slug === "given-names"
      );
      const surnameField = profileMisMatchFields?.find(
        (item: any) => item.slug === "surname"
      );
      if (profileMisMatchFields) {
        if (givenNamesField) {
          const givenNames = givenNamesField?.value?.trim().split(/\s+/);
          if (givenNames.length === 1) {
            firstName.onChange(givenNames[0]);
            middleName.onChange("");
          } else {
            const last = givenNames[givenNames?.length - 1];
            const first = givenNames?.slice(0, -1).join(" ");
            firstName.onChange(first);
            middleName.onChange(last);
          }
        }

        if (surnameField) {
          lastName.onChange(surnameField?.value);
        }
        setMisMatchFields(undefined);
      }
    },
    [firstName, middleName, lastName]
  );

  // Track selected document once
  useEffect(() => {
    dispatch(setDocCaptureData({ selectedIDDocument: document }));
  }, [dispatch, document]);

  /////// GENERATE MOBILE URL
  useEffect(() => {
    const {
      config: {
        newDocumentRequired,
        facialComparison,
        preventForcedSubmission,
      } = {},
      _id: documentId,
    } = document;

    const primaryColor = client?.branding?.primaryColor ?? "#14BDF3";

    const queryParams = new URLSearchParams({
      startScreen: String(!newDocumentRequired),
      facialComparison: String(facialComparison),
      preventForceSubmit: String(preventForcedSubmission ? true : false),
      color: primaryColor,
    });
  const url = `${window.location.origin}/document-upload/${checkId}/mobile-handoff/${documentId}/${sessionId}?${queryParams.toString()}`;
    setMobileUrl(url);
  }, [
    location,
    checkId,
    document,
    sessionId,
    client,
    questionnaire_data,
    firstName,
    lastName,
    middleName,
  ]);

  // A completed session keeps reporting isCompleted, so the id is rotated when a
  // new capture cycle starts rather than when the previous one succeeds.
  const beginNewCycle = useCallback(() => {
    if (consumedSessions.current.has(sessionId)) {
      setSessionId(uuidv4());
    }
  }, [sessionId]);

  // Stop the mobile-handoff poll once a document is committed via the desktop
  // capture path. Mirrors the teardown in the poll's onSuccess handler and
  // consumes the session so beginNewCycle() rotates to a fresh id on re-upload.
  const stopMobileHandoffPoll = useCallback(() => {
    setCheckIfDocsUploaded(false);
    setStartFetching(false);
    consumedSessions.current.add(sessionId);
  }, [sessionId]);

  const verifyDocument = useCallback(
    async ({
      docFrontSideImage,
      selfieImage,
      givenNames,
      surname,
      regulaImages,
      docBackSideImage,
    }: any) => {
   
      setMisMatchFields(undefined);
      setWarningMessage(undefined);
      setDisableStoreButton(false);
      setIsError(undefined);
      setDocumentFields(null);
      setImages(undefined);
      setMobileHandoffImages(undefined);
      setDocumentErrors(null);
      setIsVerifying(true);
      setDocKey(null);
      setFaceMisMatch(false);
      setDisableForceSubmitButton(false);

      const newDocument = docBackSideImage
        ? {
            images: [
              { format: "base64", key: "front", source: docFrontSideImage },
              { format: "base64", key: "back", source: docBackSideImage },
              { format: "base64", key: "selfie", source: selfieImage },
            ],
          }
        : {
            images: [
              { format: "base64", key: "front", source: docFrontSideImage },
              { format: "base64", key: "selfie", source: selfieImage },
            ],
          };

      try {
        const response = await axios.post(
          `${API_BASE_URL}/v1/checks/${checkId}/document-collectors/${document._id}/Validate`,
          newDocument, 
          {
            timeout: 30000
          }
        );

        setIsVerifying(false);
        setDocumentFields(response?.data?.data);
        setDocKey(response?.data?._id);
        setForceSubmit(false);
        dispatch(
          setDocCaptureData({
            identityDocument: response.data,
            submittedIDs: {
              [selectedIDDocument._id]: response.data?._id || "",
            },
          })
        );

          if(!givenNames && !surname){
            return
          }
        // name comparison validation

        let fieldsToUpdate: string[] = [];
        const isGivenNamesMatch = compareGivenNames({
          profileFirstName: firstName?.value ?? "",
          profileMiddleName: middleName?.value ?? "",
          regulaGivenNames: givenNames ?? "",
        });
        const isLastNameMatch = compareLastNames({
          profileLastName: lastName?.value ?? "",
          regulaLastName: surname ?? "",
        });

        if (!isGivenNamesMatch) {
          fieldsToUpdate.push("given-names");
          setWarningMessage(NAME_MISMATCH_MESSAGE);
        }
        if (!isLastNameMatch) {
          fieldsToUpdate.push("surname");
          setWarningMessage(NAME_MISMATCH_MESSAGE);
        }

        if (isLastNameMatch && isGivenNamesMatch) {
          setWarningMessage(undefined);
          fieldsToUpdate = [];
        }
        setMisMatchFields(fieldsToUpdate);
      } catch (error: any) {
        setIsVerifying(false);
        setDisableStoreButton(true);

        // Log error to Sentry with context
        Sentry.captureException(error, {
          tags: {
            component: 'RegulaUploadCard',
            operation: 'verifyDocument',
            documentType: document?.name?.en || 'unknown',
            documentId: document?._id || 'unknown'
          },
          extra: {
            checkId,
            documentConfig: document?.config,
            hasGivenNames: !!givenNames,
            hasSurname: !!surname,
            errorResponse: error?.response?.data,
            errorStatus: error?.response?.status
          }
        });

        if (error.response) {
          // setRegulaDocumentErrorCode(error?.response?.status.toString())
          if (error?.response?.data?.errors?.["images.selfie"]) {
            setFaceMisMatch(true);
          }
          const returnedDocument = error?.response?.data.document;
          if(returnedDocument){
            if(!document?.config?.preventForcedSubmission){
               setForceSubmit(true);
            }
          
          }
          setDocumentFields(returnedDocument?.data);
          if (!returnedDocument) {
            setIsError(
              "Something went wrong while uploading your document. Please try again. If the issue persists, you can retry once more or contact us at workright@fragomen.com."
            );
            setDisableForceSubmitButton(true);
          }

          if (error?.response?.data?.errors) {
            if(error?.response?.data?.errors?.document?.en === "We could not successfully authenticate the document. Please provide a new document." ||
                error?.response?.data?.errors?.["images.front"]?.en === "We could not successfully authenticate the document. Please provide a new document."){
              setUnsuccessful((prev) => prev + 1);
            }
          prevErrorRef.current = "auth_error";
          }

          if(!givenNames && !surname){
            return
          }
          // name comparison validation
          let fieldsToUpdate: string[] = [];
          const isGivenNamesMatch = compareGivenNames({
            profileFirstName: firstName?.value ?? "",
            profileMiddleName: middleName?.value ?? "",
            regulaGivenNames: givenNames ?? "",
          });
          const isLastNameMatch = compareLastNames({
            profileLastName: lastName?.value ?? "",
            regulaLastName: surname ?? "",
          });

          if (!isGivenNamesMatch) {
            fieldsToUpdate.push("given-names");
            setWarningMessage(NAME_MISMATCH_MESSAGE);
          }
          if (!isLastNameMatch) {
            fieldsToUpdate.push("surname");
            setWarningMessage(NAME_MISMATCH_MESSAGE);
          }

          if (isLastNameMatch && isGivenNamesMatch) {
            setWarningMessage(undefined);
            fieldsToUpdate = [];
          }
          setMisMatchFields(fieldsToUpdate);
          return;
        }
        setIsError(
          "Something went wrong while uploading your document. Please try again. If the issue persists, you can retry once more or contact us at workright@fragomen.com."
        );
        setDisableForceSubmitButton(true);
      }
    },
    [
      checkId,
      dispatch,
      document?._id,
      document?.config,
      document?.name?.en,
      firstName?.value,
      lastName?.value,
      selectedIDDocument?._id,
      middleName?.value
    ]
  );

  useEffect(() => {
    if (unsuccessful === 1) {
      setDocumentErrors({
        "images.front": {
          en: "We were unable to process your document. This may happen if the image is unclear (e.g. poor lighting, glare, shadows, or blurry text). Please check that your document is well-lit, clearly visible, and fully within the frame, then try uploading again."
        }
      });
    }

    if(unsuccessful > 1){

      const isUK = questionnaire_data?.jurisdiction?.code3 === "GBR";

      const secondAttemptMsgUK =
          "We were unable to process your document. This may happen if the image is unclear (e.g. poor lighting, glare, shadows, or blurry text). Please check again that your document is well-lit, clearly visible, and fully within the frame, then try uploading again. If you believe your document meets these requirements and the issue persists after your second attempt, please contact workright@fragomen.com for assistance.";

      const secondAttemptMsgROW =
          "We were unable to process your document. This may happen if the image is unclear (e.g. poor lighting, glare, shadows, or blurry text). Please check again that your document is well-lit, clearly visible, and fully within the frame, then try uploading again. If you believe your document meets these requirements, please click Submit for Review below. This will send your document for manual review.";

      const secondAttemptMsg = isUK ? secondAttemptMsgUK : secondAttemptMsgROW;

      setDocumentErrors({
        "images.front": {
          en: secondAttemptMsg
        }
      });
    }
  }, [unsuccessful,questionnaire_data]);

  /////////FORCE SUBMIT ID DOC TO THE API

  const forceDocument = async ({
    docFrontSideImage,
    selfieImage,
    regulaImages,
    docBackSideImage,
    profileMisMatchFields,
  }: any) => {
    setDocKey(null);
    setIsError(undefined);
    setIsVerifying(true); // Start the loader
    try {
      const newDocument = {
        images: docBackSideImage
          ? [
              { format: "base64", key: "front", source: docFrontSideImage },
              { format: "base64", key: "back", source: docBackSideImage },
              { format: "base64", key: "selfie", source: selfieImage },
            ]
          : [
              { format: "base64", key: "front", source: docFrontSideImage },
              { format: "base64", key: "selfie", source: selfieImage },
            ],
        status: "created",
      };
      const response = await axios.post(
        `${API_BASE_URL}/v1/checks/${checkId}/document-collectors/${document._id}/Validate`,
        newDocument
      );
      setIsVerifying(false);
      setDocKey(response?.data?._id);

      dispatch(
        setDocCaptureData({
          identityDocumentForced: response?.data,
          submittedIDs: {
            [selectedIDDocument?._id]: response?.data?._id,
          },
        })
      );
      if (response?.data?._id) {
        dispatch(addIdentityDoc({ ...document, doc_key: response?.data?._id }));
      }
      updateNamesFromProfile(profileMisMatchFields);
      return Promise.resolve(response.data._id);
    } catch (error: any) {
      setIsVerifying(false);
      setDisableStoreButton(true);

      // Log error to Sentry with context
      Sentry.captureException(error, {
        tags: {
          component: 'RegulaUploadCard',
          operation: 'forceDocument',
          documentType: document?.name?.en || 'unknown',
          documentId: document?._id || 'unknown'
        },
        extra: {
          checkId,
          documentConfig: document?.config,
          profileMisMatchFields,
          errorResponse: error?.response?.data,
          errorStatus: error?.response?.status
        }
      });

      if (error.response) {
        const returnedDocument = error?.response?.data.document;
        if (returnedDocument) {
          setDocumentFields(returnedDocument?.data);
          setDocKey(returnedDocument?._id);
          if (returnedDocument?._id) {
            dispatch(
              addIdentityDoc({ ...document, doc_key: returnedDocument?._id })
            );
          }

          dispatch(
            setDocCaptureData({
              identityDocumentForced: returnedDocument,
              submittedIDs: {
                [selectedIDDocument?._id]: returnedDocument?._id,
              },
            })
          );

          updateNamesFromProfile(profileMisMatchFields);
          setMisMatchFields(undefined);
          return Promise.resolve(docKey);
        }

        if (error?.response?.data?.errors) {
          setDocumentErrors(error?.response?.data?.errors);
        }
      }
      return Promise.reject(error);
    }
  };

  // Delay starting the fetch by `x` milliseconds after `checkIfDocsUploaded` is true
  useEffect(() => {
    if (checkIfDocsUploaded && !isMobile) {
      const firstTimeout = setTimeout(() => {
        setStartFetching(true);

        if (mobileUploadStatus === "initiated") {
          const secondTimeout = setTimeout(() => {
            setMobileUploadStatus("loading");
          }, 15000);

          return () => clearTimeout(secondTimeout);
        }
      }, 3000);

      // Cleanup for first timeout
      return () => clearTimeout(firstTimeout);
    } else if (!checkIfDocsUploaded) {
      setStartFetching(false);
    }
  }, [checkIfDocsUploaded, isMobile, mobileUploadStatus]);

  useEffect(() => {
    if (mobileUploadStatus === "loading") {
      if (!checkIfDocsUploaded) {
        setStartFetching(false);
        setMobileUploadStatus("initiated");
      }
    }
  }, [checkIfDocsUploaded, mobileUploadStatus]);

  const fetcher = async (url: string) => {
    const response = await fetch(url);
    if (!response.ok) {
      const pollError: any = new Error(
        `Mobile handoff poll failed with status ${response.status}`
      );
      pollError.status = response.status;
      throw pollError;
    }
    return response.json();
  };

  useSWR(
    startFetching && sessionId && !consumedSessions.current.has(sessionId)
      ? `${API_BASE_URL}/v1/checks/${checkId}/document-collectors/${document._id}/mobile-handoff-sessions/${sessionId}`
      : null,
    fetcher,
    {
      refreshInterval: 2000,
      errorRetryCount: SWR_RETRY_COUNT,
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        if (retryCount >= SWR_RETRY_COUNT) {
          setMobileUploadStatus("error");
          
          // Log SWR polling error to Sentry
          Sentry.captureException(error, {
            tags: {
              component: 'RegulaUploadCard',
              operation: 'mobileHandoffPolling',
              documentType: document?.name?.en || 'unknown',
              documentId: document?._id || 'unknown'
            },
            extra: {
              checkId,
              sessionId,
              retryCount,
              maxRetries: SWR_RETRY_COUNT,
              swrKey: key
            }
          });
          
          return;
        }
        // Client errors (e.g. 400) are terminal: retrying a malformed or
        // invalid request will never succeed, so surface it immediately
        // instead of waiting out the full retry window. 408 (Request Timeout)
        // and 429 (Too Many Requests) are retryable, so let them fall through.
        if (
          error.status >= 400 &&
          error.status < 500 &&
          error.status !== 408 &&
          error.status !== 429
        ) {
          setMobileUploadStatus("error");

          // Log SWR polling error to Sentry
          Sentry.captureException(error, {
            tags: {
              component: 'RegulaUploadCard',
              operation: 'mobileHandoffPolling',
              documentType: document?.name?.en || 'unknown',
              documentId: document?._id || 'unknown'
            },
            extra: {
              checkId,
              sessionId,
              retryCount,
              maxRetries: SWR_RETRY_COUNT,
              swrKey: key
            }
          });

          return;
        }
        setTimeout(() => revalidate({ retryCount }), 5000);
      },
      onSuccess: (data) => {
        if (data) {
          if (data?.isCompleted) {
            setCheckIfDocsUploaded(false);
            dispatch(addIdentityDoc({ ...document, doc_key: data.subjectId }));
            setStartFetching(false);
            const returnedDocument = data?.results?.document;
            setMobileUploadStatus("success");
            consumedSessions.current.add(sessionId);
            if (returnedDocument) {
              if(returnedDocument?.images){
                setMobileHandoffImages(returnedDocument?.images);
                setImages(returnedDocument?.images);
              }
              if (returnedDocument?.data) {
                setDocumentFields(returnedDocument?.data);
                setMobileHandoffFields(returnedDocument?.data);
                setIsError(undefined)
                setDocumentErrors(undefined)

                // If no data is returned, exit early and don't do name comparison
                if(returnedDocument?.data.length === 0) return;

                const givenNames = findRegulaFieldBySlug(
                  returnedDocument?.data,
                  "given-names"
                );
                const surname = findRegulaFieldBySlug(
                  returnedDocument?.data,
                  "surname"
                );
                if(!givenNames && !surname) return;
                let fieldsToUpdate: string[] = [];
                const isGivenNamesMatch = compareGivenNames({
                  profileFirstName: firstName?.value ?? "",
                  profileMiddleName: middleName?.value ?? "",
                  regulaGivenNames: givenNames?.value ?? ""
                });
                const isLastNameMatch = compareLastNames({
                  profileLastName: lastName?.value ?? "",
                  regulaLastName: surname?.value ?? ""
                });

                if (!isGivenNamesMatch) {
                  fieldsToUpdate.push("given-names");
                  setWarningMessage(NAME_MISMATCH_MESSAGE);
                }

                if (!isLastNameMatch) {
                  fieldsToUpdate.push("surname");
                  setWarningMessage(NAME_MISMATCH_MESSAGE);
                }

                if (isLastNameMatch && isGivenNamesMatch) {
                  setWarningMessage(undefined);
                  fieldsToUpdate = [];
                }

                setMisMatchFields(fieldsToUpdate);
              }
            }
          }
        }
      },
    }
  );

  return (
    <div>
      <div className="mb-1 font-semibold">
        {document.name.en || ""}{" "}
        <span className="">{document.config.required ? "*" : ""}</span>
      </div>
      <RegulaDocumentUpload
        regulaLicense={process.env.REACT_APP_REGULA_LICENSE_KEY}
        onMobileSessionId={() => { /* the session id is owned by this component */ }}
        pollApi={() => {
          beginNewCycle();
          setMobileUploadStatus("initiated");
          setCheckIfDocsUploaded(true);
        }}
        onReplace={()=>{
            beginNewCycle();
            setCheckIfDocsUploaded(true);
            setMobileUploadStatus("initiated");
        }}
        mobileHandOffFields={mobileHandoffFields}
        mobileHandoffImages={mobileHandoffImages}
        disableForceSubmitButton={disableForceSubmitButton}
        error={error}
        profileMisMatchFields={misMatchFields}
        warning={warningMessage}
        onOpenUploader={() => {
          beginNewCycle();
          setCheckIfDocsUploaded(true);
        }}
        onChangeToDesktop={() => {
          // Keep the mobile-handoff poll alive. The desktop capture is a
          // dismissable overlay on top of the same still-mounted QR session,
          // and the vendor fires no callback when it closes. Tearing the poll
          // down here would leave a live QR that is no longer polled once the
          // overlay is dismissed.
          setMobileUploadStatus("initiated");
        }}
        onCloseUploader={() => {
          setDocumentFields(undefined);
        }}
        onForceDocument={async (fields: any, profileMisMatchFields: any) => {
          await forceDocument({
            docFrontSideImage:  fields?.docFrontSideImage?.split(",")[1],
            selfieImage: fields?.selfieImage,
            profileMisMatchFields,
          });
            stopMobileHandoffPoll();
            if(!document?.config?.preventForcedSubmission){
               setForceSubmit(false);
            }
            if(fields){
              dispatch(
              setDocumentValidationError({
                firstNameInDocument: fields?.givenNames?.value,
                lastNameInDocument: fields?.surname?.value,
              })
            );
           }
        }}
        onStoreDocument={(fields: any, profileMisMatchFields: any) => {
          if (docKey) {
            dispatch(addIdentityDoc({ ...document, doc_key: docKey }));
            stopMobileHandoffPoll();
          }
          updateNamesFromProfile(profileMisMatchFields);
          setMisMatchFields(undefined);
          if(fields){
          dispatch(
          setDocumentValidationError({
            firstNameInDocument: fields?.givenNames?.value,
            lastNameInDocument: fields?.surname?.value,
          })
        );
        }
        }}
        disableSaveButton={disableStoreButton}
        images={images}
        mobileHandoffUrl={mobileUrl}
        uploadAttempts={1}
        title={document.name.en || ""}
        facialComparisonMisMatch={faceMisMatch}
        facialComparison={document?.config?.facialComparison}
        color={
          client && client?.branding?.primaryColor
            ? client?.branding?.primaryColor
            : "#14BDF3"
        }
        documentFields={documentFields}
        documentFieldsErrors={documentErrors}
        isVerifying={isVerifying}
        settings={{
          startScreen: !document?.config?.newDocumentRequired,
        }}
        onRemove={() => {
          dispatch(
            setDocumentValidationError({
              disableFirstName: false,
              disableLastName: false,
            })
          );
          beginNewCycle();
          setMobileUploadStatus("initiated");
          setDocumentFields(null);
          if (removeDocument) removeDocument(document, "identityDocuments");
        }}
        mobileHandoOffStatus={mobileUploadStatus as any}
        forceSubmit={forceSubmit}
        onProcessedFinished={async (fields: any) => {
          await verifyDocument({
            docFrontSideImage:  fields?.docFrontSideImage?.split(",")[1],
            selfieImage: fields?.selfieImage,
            givenNames: fields?.givenNames?.value,
            surname: fields?.surname?.value,
            middleName: fields?.middleName?.value,
          });
        }}
      />
    </div>
  );
};

export default RegulaUploadCard;
